import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDocs, 
  getDoc, 
  query, 
  where, 
  orderBy,
  Timestamp 
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import { FormTemplate, ListTemplate } from '../types/formBuilder';

const FORM_TEMPLATES_COLLECTION = 'formTemplates';
const LIST_TEMPLATES_COLLECTION = 'listTemplates';

export class TemplateService {
  // Helper function to safely convert Firestore timestamp to Date
  private static convertTimestamp(timestamp: any): Date {
    if (!timestamp) return new Date();
    
    // If it's a proper Firestore Timestamp with toDate method
    if (typeof timestamp.toDate === 'function') {
      return timestamp.toDate();
    }
    
    // If it's a plain object with seconds property (serialized timestamp)
    if (timestamp.seconds) {
      return new Date(timestamp.seconds * 1000);
    }
    
    // If it's already a Date or date string
    if (timestamp instanceof Date) {
      return timestamp;
    }
    
    // Try to parse as date string
    try {
      return new Date(timestamp);
    } catch {
      return new Date();
    }
  }

  /**
   * Export form template as JSON
   */
  static exportFormTemplate(template: FormTemplate): string {
    const exportData = {
      ...template,
      exportedAt: new Date().toISOString(),
      exportVersion: '1.0'
    };
    
    return JSON.stringify(exportData, null, 2);
  }

  /**
   * Import form template from JSON
   */
  static importFormTemplate(jsonData: string): Partial<FormTemplate> {
    try {
      const templateData = JSON.parse(jsonData);
      
      // Remove system fields that shouldn't be imported
      const {
        id,
        createdAt,
        updatedAt,
        createdBy,
        createdByName,
        organizationId,
        exportedAt,
        exportVersion,
        ...importableData
      } = templateData;
      
      // Add import suffix to name
      if (importableData.name) {
        importableData.name = `${importableData.name} (importált)`;
      }
      
      // Ensure required fields have default values
      return {
        ...importableData,
        version: 1,
        isActive: true,
        fields: importableData.fields || [],
        sections: importableData.sections || [],
        settings: {
          submitButtonText: 'Submit',
          cancelButtonText: 'Cancel',
          showProgressBar: false,
          allowDrafts: true,
          multiStep: false,
          ...importableData.settings
        },
        permissions: {
          canView: [],
          canEdit: [],
          canSubmit: [],
          ...importableData.permissions
        }
      };
    } catch (error) {
      throw new Error(`Invalid form template JSON: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Export multiple form templates as JSON
   */
  static exportMultipleFormTemplates(templates: FormTemplate[]): string {
    const exportData = {
      templates: templates.map(template => ({
        ...template,
        exportedAt: new Date().toISOString(),
        exportVersion: '1.0'
      })),
      exportedAt: new Date().toISOString(),
      exportVersion: '1.0',
      count: templates.length
    };
    
    return JSON.stringify(exportData, null, 2);
  }

  /**
   * Import multiple form templates from JSON
   */
  static importMultipleFormTemplates(jsonData: string): Partial<FormTemplate>[] {
    try {
      const data = JSON.parse(jsonData);
      
      // Check if it's a single template or multiple templates
      if (data.templates && Array.isArray(data.templates)) {
        // Multiple templates format
        return data.templates.map((templateData: any, index: number) => {
          const {
            id,
            createdAt,
            updatedAt,
            createdBy,
            createdByName,
            organizationId,
            exportedAt,
            exportVersion,
            ...importableData
          } = templateData;
          
          // Add import suffix to name
          if (importableData.name) {
            importableData.name = `${importableData.name} (importált ${index + 1})`;
          }
          
          return {
            ...importableData,
            version: 1,
            isActive: true,
            fields: importableData.fields || [],
            sections: importableData.sections || [],
            settings: {
              submitButtonText: 'Submit',
              cancelButtonText: 'Cancel',
              showProgressBar: false,
              allowDrafts: true,
              multiStep: false,
              ...importableData.settings
            },
            permissions: {
              canView: [],
              canEdit: [],
              canSubmit: [],
              ...importableData.permissions
            }
          };
        });
      } else {
        // Single template format - convert to array
        return [this.importFormTemplate(jsonData)];
      }
    } catch (error) {
      throw new Error(`Invalid form templates JSON: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  // Helper function to remove undefined values
  private static cleanData(obj: any): any {
    if (obj === null || obj === undefined) {
      return null;
    }
    
    // Preserve Firebase Timestamp objects
    if (obj && typeof obj === 'object' && obj.constructor && obj.constructor.name === 'Timestamp') {
      return obj;
    }
    
    if (Array.isArray(obj)) {
      return obj.map(item => this.cleanData(item)).filter(item => item !== undefined);
    }
    
    if (typeof obj === 'object') {
      const cleaned: any = {};
      for (const [key, value] of Object.entries(obj)) {
        if (value !== undefined) {
          cleaned[key] = this.cleanData(value);
        }
      }
      return cleaned;
    }
    
    return obj;
  }

  // Form Templates
  static async createFormTemplate(template: Omit<FormTemplate, 'id'>): Promise<FormTemplate> {
    try {
      const createdAt = Timestamp.now();
      const updatedAt = Timestamp.now();
      
      const cleanedTemplate = this.cleanData({
        ...template,
        version: 1,
        isActive: template.isActive ?? true,
        organizationId: template.organizationId || '',
        createdBy: template.createdBy || '',
        createdByName: template.createdByName || '',
        name: template.name || 'Untitled Form',
        fields: template.fields || [],
        sections: template.sections || [],
        settings: {
          title: template.settings?.title || '',
          description: template.settings?.description || '',
          submitButtonText: template.settings?.submitButtonText || 'Submit',
          cancelButtonText: template.settings?.cancelButtonText || 'Cancel',
          showProgressBar: template.settings?.showProgressBar ?? false,
          allowDrafts: template.settings?.allowDrafts ?? false,
          multiStep: template.settings?.multiStep ?? false,
          ...template.settings
        },
        permissions: {
          ...template.permissions,
          canView: template.permissions?.canView || [],
          canEdit: template.permissions?.canEdit || [],
          canSubmit: template.permissions?.canSubmit || []
        }
      });

      // Remove any existing timestamp fields from cleaned template
      const { createdAt: _, updatedAt: __, ...cleanedWithoutTimestamps } = cleanedTemplate;
      
      const templateData = {
        ...cleanedWithoutTimestamps,
        createdAt,
        updatedAt
      };

      const docRef = await addDoc(collection(db, FORM_TEMPLATES_COLLECTION), templateData);
      
      return {
        ...templateData,
        id: docRef.id,
        createdAt: createdAt.toDate(),
        updatedAt: updatedAt.toDate()
      } as FormTemplate;
    } catch (error) {
      console.error('Error creating form template:', error);
      throw new Error('Failed to create form template');
    }
  }

  static async updateFormTemplate(id: string, template: Partial<FormTemplate>): Promise<void> {
    try {
      const docRef = doc(db, FORM_TEMPLATES_COLLECTION, id);
      const updatedAt = Timestamp.now();
      
      const cleanedTemplate = this.cleanData({
        ...template,
        version: (template.version || 0) + 1
      });

      const updateData = {
        ...cleanedTemplate,
        updatedAt
      };

      await updateDoc(docRef, updateData);
    } catch (error) {
      console.error('Error updating form template:', error);
      throw new Error('Failed to update form template');
    }
  }

  static async deleteFormTemplate(id: string): Promise<void> {
    try {
      const docRef = doc(db, FORM_TEMPLATES_COLLECTION, id);
      await deleteDoc(docRef);
    } catch (error) {
      console.error('Error deleting form template:', error);
      throw new Error('Failed to delete form template');
    }
  }


  // Get public form template by publicId
  static async getPublicFormTemplate(publicId: string): Promise<FormTemplate | null> {
    try {
      const q = query(
        collection(db, FORM_TEMPLATES_COLLECTION),
        where('publicSettings.isPublic', '==', true),
        where('publicSettings.publicId', '==', publicId),
        where('isActive', '==', true)
      );
      
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        return null;
      }
      
      const doc = querySnapshot.docs[0];
      const data = doc.data();
      
      return {
        id: doc.id,
        ...data,
        createdAt: this.convertTimestamp(data.createdAt),
        updatedAt: this.convertTimestamp(data.updatedAt)
      } as FormTemplate;
    } catch (error) {
      console.error('Error getting public form template:', error);
      throw error;
    }
  }

  static async getFormTemplates(organizationId?: string): Promise<FormTemplate[]> {
    try {
      let q = query(
        collection(db, FORM_TEMPLATES_COLLECTION),
        orderBy('updatedAt', 'desc')
      );

      if (organizationId) {
        q = query(
          collection(db, FORM_TEMPLATES_COLLECTION),
          where('organizationId', '==', organizationId),
          orderBy('updatedAt', 'desc')
        );
      }

      const querySnapshot = await getDocs(q);
      const templates: FormTemplate[] = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        
        // Safely convert Firestore timestamps to Date objects
        const createdAt = this.convertTimestamp(data.createdAt);
        const updatedAt = this.convertTimestamp(data.updatedAt);
        
        templates.push({
          ...data,
          id: doc.id,
          createdAt,
          updatedAt
        } as FormTemplate);
      });

      return templates;
    } catch (error) {
      console.error('Error getting form templates:', error);
      throw new Error('Failed to get form templates');
    }
  }

  // Get single form template
  static async getFormTemplate(templateId: string): Promise<FormTemplate> {
    try {
      const docRef = doc(db, FORM_TEMPLATES_COLLECTION, templateId);
      const docSnap = await getDoc(docRef);
      
      if (!docSnap.exists()) {
        throw new Error('Form template not found');
      }
      
      const data = docSnap.data();
      return {
        id: docSnap.id,
        ...data,
        createdAt: this.convertTimestamp(data.createdAt),
        updatedAt: this.convertTimestamp(data.updatedAt)
      } as FormTemplate;
    } catch (error) {
      console.error('Error getting form template:', error);
      throw new Error('Failed to get form template');
    }
  }

  // Get single list template
  static async getListTemplate(templateId: string): Promise<ListTemplate> {
    try {
      const docRef = doc(db, 'listTemplates', templateId);
      const docSnap = await getDoc(docRef);
      
      if (!docSnap.exists()) {
        throw new Error('List template not found');
      }
      
      const data = docSnap.data();
      return {
        id: docSnap.id,
        ...data,
        createdAt: this.convertTimestamp(data.createdAt),
        updatedAt: this.convertTimestamp(data.updatedAt)
      } as ListTemplate;
    } catch (error) {
      console.error('Error getting list template:', error);
      throw new Error('Failed to get list template');
    }
  }

  // Get lists for a specific form template
  static async getListsForForm(formTemplateId: string): Promise<ListTemplate[]> {
    try {
      const q = query(
        collection(db, 'listTemplates'),
        where('formTemplateId', '==', formTemplateId),
        orderBy('isDefaultList', 'desc'), // Default lists first
        orderBy('createdAt', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: this.convertTimestamp(doc.data().createdAt),
        updatedAt: this.convertTimestamp(doc.data().updatedAt)
      } as ListTemplate));
    } catch (error) {
      console.error('Error getting lists for form:', error);
      throw new Error('Failed to get lists for form');
    }
  }

  // Create default list for form template
  static async createDefaultListForForm(formTemplate: FormTemplate): Promise<ListTemplate> {
    const defaultList: Omit<ListTemplate, 'id'> = {
      name: `${formTemplate.name} - Default List`,
      description: `Default list view for ${formTemplate.name} form submissions`,
      formTemplateId: formTemplate.id,
      isDefaultList: true,
      dataSource: {
        type: 'form_submissions',
        formTemplateId: formTemplate.id
      },
      columns: this.generateColumnsFromForm(formTemplate),
      filters: [],
      settings: {
        pageSize: 25,
        showSearch: true,
        showFilters: true,
        showExport: true,
        allowSelection: false,
        allowInlineEdit: false
      },
      permissions: {
        canView: formTemplate.permissions.canView,
        canEdit: formTemplate.permissions.canEdit,
        canDelete: formTemplate.permissions.canEdit // Use edit permission for delete
      },
      organizationId: formTemplate.organizationId,
      createdBy: formTemplate.createdBy,
      createdByName: formTemplate.createdByName,
      createdAt: new Date(),
      updatedAt: new Date(),
      isActive: true
    };

    return this.createListTemplate(defaultList);
  }

  // Generate columns from form fields
  private static generateColumnsFromForm(formTemplate: FormTemplate): any[] {
    const columns: any[] = [];
    
    // Add submission metadata columns
    columns.push({
      id: 'createdAt',
      field: 'createdAt',
      label: 'Submitted At',
      type: 'datetime',
      sortable: true,
      filterable: true,
      searchable: false,
      order: 0
    });

    columns.push({
      id: 'submittedBy',
      field: 'submittedByName',
      label: 'Submitted By',
      type: 'user',
      sortable: true,
      filterable: true,
      searchable: true,
      order: 1
    });

    // Add columns for form fields
    formTemplate.fields.forEach((field, index) => {
      let columnType: string = 'text';
      
      // Map form field types to column types
      switch (field.type) {
        case 'number':
        case 'currency':
          columnType = field.type;
          break;
        case 'date':
          columnType = 'date';
          break;
        case 'email':
        case 'phone':
        case 'text':
        case 'textarea':
          columnType = 'text';
          break;
        case 'select':
        case 'radio':
          columnType = 'text';
          break;
        case 'checkbox':
        case 'multiselect':
          columnType = 'text';
          break;
        case 'switch':
          columnType = 'boolean';
          break;
        case 'file':
        case 'image':
          columnType = 'link';
          break;
        default:
          columnType = 'text';
      }

      columns.push({
        id: field.id,
        field: field.name,
        label: field.label,
        type: columnType,
        sortable: true,
        filterable: ['select', 'radio', 'checkbox', 'switch'].includes(field.type),
        searchable: ['text', 'textarea', 'email'].includes(field.type),
        order: index + 2
      });
    });

    return columns;
  }

  // List Templates
  static async createListTemplate(template: Omit<ListTemplate, 'id'>): Promise<ListTemplate> {
    try {
      const createdAt = Timestamp.now();
      const updatedAt = Timestamp.now();
      
      const cleanedTemplate = this.cleanData({
        ...template,
        version: 1,
        isActive: template.isActive ?? true,
        organizationId: template.organizationId || '',
        createdBy: template.createdBy || '',
        createdByName: template.createdByName || '',
        name: template.name || 'Untitled List',
        columns: template.columns || [],
        settings: {
          pageSize: template.settings?.pageSize || 10,
          showSearch: template.settings?.showSearch ?? true,
          showFilters: template.settings?.showFilters ?? true,
          showExport: template.settings?.showExport ?? true,
          allowSelection: template.settings?.allowSelection ?? false,
          allowInlineEdit: template.settings?.allowInlineEdit ?? false,
          ...template.settings
        },
        permissions: {
          ...template.permissions,
          canView: template.permissions?.canView || [],
          canEdit: template.permissions?.canEdit || [],
          canDelete: template.permissions?.canDelete || []
        }
      });

      // Remove any existing timestamp fields from cleaned template
      const { createdAt: _, updatedAt: __, ...cleanedWithoutTimestamps } = cleanedTemplate;
      
      const templateData = {
        ...cleanedWithoutTimestamps,
        createdAt,
        updatedAt
      };

      const docRef = await addDoc(collection(db, LIST_TEMPLATES_COLLECTION), templateData);
      
      return {
        ...templateData,
        id: docRef.id,
        createdAt: createdAt.toDate(),
        updatedAt: updatedAt.toDate()
      } as ListTemplate;
    } catch (error) {
      console.error('Error creating list template:', error);
      throw new Error('Failed to create list template');
    }
  }

  static async updateListTemplate(id: string, template: Partial<ListTemplate>): Promise<void> {
    try {
      const docRef = doc(db, LIST_TEMPLATES_COLLECTION, id);
      const updatedAt = Timestamp.now();
      
      const cleanedTemplate = this.cleanData({
        ...template,
        version: (template.version || 0) + 1
      });

      const updateData = {
        ...cleanedTemplate,
        updatedAt
      };

      await updateDoc(docRef, updateData);
    } catch (error) {
      console.error('Error updating list template:', error);
      throw new Error('Failed to update list template');
    }
  }

  static async deleteListTemplate(id: string): Promise<void> {
    try {
      const docRef = doc(db, LIST_TEMPLATES_COLLECTION, id);
      await deleteDoc(docRef);
    } catch (error) {
      console.error('Error deleting list template:', error);
      throw new Error('Failed to delete list template');
    }
  }


  static async getListTemplates(organizationId?: string): Promise<ListTemplate[]> {
    try {
      let q = query(
        collection(db, LIST_TEMPLATES_COLLECTION),
        orderBy('updatedAt', 'desc')
      );

      if (organizationId) {
        q = query(
          collection(db, LIST_TEMPLATES_COLLECTION),
          where('organizationId', '==', organizationId),
          orderBy('updatedAt', 'desc')
        );
      }

      const querySnapshot = await getDocs(q);
      const templates: ListTemplate[] = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        
        // Safely convert Firestore timestamps to Date objects
        const createdAt = this.convertTimestamp(data.createdAt);
        const updatedAt = this.convertTimestamp(data.updatedAt);
        
        templates.push({
          ...data,
          id: doc.id,
          createdAt,
          updatedAt
        } as ListTemplate);
      });

      return templates;
    } catch (error) {
      console.error('Error getting list templates:', error);
      throw new Error('Failed to get list templates');
    }
  }
}
